const STORAGE_KEY = "room.contact.vault.v1";

export type LocalContact = {
  id: string;
  pseudo: string;
  numero: string;
};

type VaultRecord = {
  version: 1;
  salt: string;
  iv: string;
  data: string;
  verifier: string;
  panicVerifier: string;
};

const enc = new TextEncoder();
const dec = new TextDecoder();

// The decrypted AES key exists only in JS memory while the vault is unlocked.
// The user's password is never retained after unlock.
let sessionKey: CryptoKey | null = null;

function b64(bytes: Uint8Array): string {
  let s = "";
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s);
}

function unb64(value: string): Uint8Array {
  const s = atob(value);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

async function derive(secret: string, salt: Uint8Array): Promise<CryptoKey> {
  const secretBytes = new Uint8Array(enc.encode(secret));
  const base = await crypto.subtle.importKey(
    "raw",
    secretBytes.buffer,
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: new Uint8Array(salt).buffer,
      iterations: 250_000,
      hash: "SHA-256",
    },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function verifier(secret: string, salt: Uint8Array): Promise<string> {
  const key = await derive(secret, salt);
  const iv = new Uint8Array(12);

  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv).buffer },
    key,
    enc.encode("ROOM_CONTACT_VAULT"),
  );

  return b64(new Uint8Array(cipher));
}

function load(): VaultRecord | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? (JSON.parse(raw) as VaultRecord) : null;
}

export function hasContactVault(): boolean {
  return !!load();
}

export function isContactVaultUnlocked(): boolean {
  return sessionKey !== null;
}

export async function createContactVault(
  password: string,
  panicPassword: string,
): Promise<void> {
  if (!password || password.length < 8) {
    throw new Error("Le mot de passe doit contenir au moins 8 caractères.");
  }

  if (
    !panicPassword ||
    panicPassword.length < 8 ||
    panicPassword === password
  ) {
    throw new Error(
      "Le mot de passe PANIC doit être différent et contenir au moins 8 caractères.",
    );
  }

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await derive(password, salt);

  const data = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv).buffer },
    key,
    enc.encode(JSON.stringify([])),
  );

  const record: VaultRecord = {
    version: 1,
    salt: b64(salt),
    iv: b64(iv),
    data: b64(new Uint8Array(data)),
    verifier: await verifier(password, salt),
    panicVerifier: await verifier(panicPassword, salt),
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));

  // Keep only the CryptoKey in memory. Never keep the password.
  sessionKey = key;
}

export async function openContactVault(
  password: string,
): Promise<{ contacts: LocalContact[]; panic: boolean }> {
  const record = load();

  if (!record) {
    throw new Error("Coffre de contacts non initialisé.");
  }

  const salt = unb64(record.salt);
  const supplied = await verifier(password, salt);

  if (supplied === record.panicVerifier) {
    sessionKey = null;
    localStorage.removeItem(STORAGE_KEY);
    return { contacts: [], panic: true };
  }

  if (supplied !== record.verifier) {
    throw new Error("Mot de passe incorrect.");
  }

  const key = await derive(password, salt);

  const clear = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: new Uint8Array(unb64(record.iv)).buffer,
    },
    key,
    new Uint8Array(unb64(record.data)).buffer,
  );

  sessionKey = key;

  return {
    contacts: JSON.parse(dec.decode(clear)) as LocalContact[],
    panic: false,
  };
}

export async function saveContactVault(
  contacts: LocalContact[],
): Promise<void> {
  const record = load();

  if (!record) {
    throw new Error("Coffre de contacts non initialisé.");
  }

  if (!sessionKey) {
    throw new Error("Le coffre est verrouillé.");
  }

  const iv = crypto.getRandomValues(new Uint8Array(12));

  const data = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv).buffer },
    sessionKey,
    enc.encode(JSON.stringify(contacts)),
  );

  record.iv = b64(iv);
  record.data = b64(new Uint8Array(data));

  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
}

export function lockContactVault(): void {
  sessionKey = null;
}

export function destroyContactVault(): void {
  sessionKey = null;
  localStorage.removeItem(STORAGE_KEY);
}
